import React, { useRef, useState } from 'react';
import { ChevronRight, ChevronDown, CircleArrowRight, X } from 'lucide-react';
import Select from 'react-select';
import ErrorMessage from '../../components/common/ErrorMessage/ErrorMessage';
import { DropdownIndicator, ClearIndicator } from '../../components/common/SelectComponents';
import { getSelectStyles, formatOptionLabel } from '../../utils/selectStyles';
import { calculatePlacement } from '../../utils/dropdownPositionUtils';

const SchemaPair = ({
    pair,
    index,
    showRemove,
    sourceSchemaOptions,
    targetSchemaOptions,
    loadingSchemas,
    schemasAvailable,
    connectionsReady,
    errors,
    inputStates,
    onRemove,
    onPairChange,
    onToggleFilter,
    onAddChip,
    onRemoveChip,
    onInputChange,
    onInputKeyPress,
    modalBodyRef
}) => {

    const [sourcePlacement, setSourcePlacement] = useState('bottom');
    const [targetPlacement, setTargetPlacement] = useState('bottom');

    const sourceSelectRef = useRef(null);
    const targetSelectRef = useRef(null);

    const calculatePlacement2 = (ref) => {
        if (!ref?.current) return 'bottom';
        const rect = ref.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        return spaceBelow < 335 && spaceAbove > spaceBelow ? 'top' : 'bottom';
    };

    return (
        <div className="schema-pair-wrapper">
            <div className="schema-pair-container">

                {showRemove && (
                    <button
                        type="button"
                        className="remove-pair-btn"
                        onClick={() => onRemove(index)}
                        title="Remove schema pair"
                    >
                        <div className="remove-icon"></div>
                    </button>
                )}

                <div className="schema-pair-row">
                    <div className="schema-pair-selects">
                        <div className="form-group">
                            <label>
                                Source Schema {(!loadingSchemas && connectionsReady) && <span className="required">*</span>}
                            </label>
                            <div ref={sourceSelectRef}>
                                <Select
                                    value={sourceSchemaOptions.find(o => o.value === pair.sourceSchema) || null}
                                    onChange={(opt) => onPairChange(index, 'sourceSchema', opt ? opt.value : '')}
                                    options={sourceSchemaOptions}
                                    placeholder={
                                        loadingSchemas ? 'Loading schemas...' :
                                            !schemasAvailable ? 'No schemas available' :
                                                'Select Source Schema'
                                    }
                                    isDisabled={loadingSchemas || !schemasAvailable}
                                    isLoading={loadingSchemas}
                                    isSearchable
                                    isClearable
                                    formatOptionLabel={formatOptionLabel}
                                    styles={getSelectStyles(!!errors[`sourceSchema_${index}`])}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    menuPlacement={sourcePlacement}
                                    onMenuOpen={() => setSourcePlacement(calculatePlacement(sourceSelectRef, sourceSchemaOptions.length))}
                                    menuShouldScrollIntoView={false}
                                    closeMenuOnScroll={(e) => modalBodyRef?.current?.contains(e.target)}
                                    noOptionsMessage={() => 'No schemas found'}
                                    components={{ DropdownIndicator, ClearIndicator }}
                                />
                            </div>

                            <ErrorMessage message={errors[`sourceSchema_${index}`]} />
                        </div>

                        <div className="arrow-icon">
                            <ChevronRight style={{ width: '1.302vw', height: '1.302vw' }} color="#94a3b8" />
                        </div>

                        <div className="form-group">
                            <label>
                                Target Schema {(!loadingSchemas && connectionsReady) && <span className="required">*</span>}
                            </label>
                            <div ref={targetSelectRef}>
                                <Select
                                    value={targetSchemaOptions.find(o => o.value === pair.targetSchema) || null}
                                    onChange={(opt) => onPairChange(index, 'targetSchema', opt ? opt.value : '')}
                                    options={targetSchemaOptions}
                                    placeholder={
                                        loadingSchemas ? 'Loading schemas...' :
                                            !schemasAvailable ? 'No schemas available' :
                                                'Select Target Schema'
                                    }
                                    isDisabled={loadingSchemas || !schemasAvailable}
                                    isLoading={loadingSchemas}
                                    isSearchable
                                    isClearable
                                    formatOptionLabel={formatOptionLabel}
                                    styles={getSelectStyles(!!errors[`targetSchema_${index}`])}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    menuPlacement={targetPlacement}
                                    onMenuOpen={() => setTargetPlacement(calculatePlacement(targetSelectRef, targetSchemaOptions.length))}
                                    menuShouldScrollIntoView={false}
                                    closeMenuOnScroll={(e) => modalBodyRef?.current?.contains(e.target)}
                                    noOptionsMessage={() => 'No schemas found'}
                                    components={{ DropdownIndicator, ClearIndicator }}
                                />
                            </div>

                            <ErrorMessage message={errors[`targetSchema_${index}`]} />
                        </div>
                    </div>

                    <div className="schema-pair-actions">
                        <label className="add-filter-checkbox">
                            <input
                                type="checkbox"
                                checked={pair.addFilter}
                                onChange={() => onToggleFilter(index)}
                                disabled={loadingSchemas || !schemasAvailable}
                            />
                            <span>Add Filter</span>
                        </label>

                        {pair.addFilter && (
                            <button
                                type="button"
                                className="toggle-filter-btn"
                                onClick={() => onPairChange(index, 'filterExpanded', !pair.filterExpanded)}
                            >
                                <ChevronDown
                                    style={{ width: '1.042vw', height: '1.042vw' }}
                                    className={`chevron-icon ${pair.filterExpanded ? 'expanded' : ''}`}
                                    color="#1e40af"
                                />
                            </button>
                        )}
                    </div>
                </div>

                {/* Filter Section */}
                {pair.addFilter && pair.filterExpanded && (
                    <div className="filter-section">
                        <div className="filter-tables">
                            <FilterTable
                                title="Include"
                                pairIndex={index}
                                prefixKey="includePrefix"
                                suffixKey="includeSuffix"
                                prefixChips={pair.includePrefix}
                                suffixChips={pair.includeSuffix}
                                inputStates={inputStates}
                                onAddChip={onAddChip}
                                onRemoveChip={onRemoveChip}
                                onInputChange={onInputChange}
                                onInputKeyPress={onInputKeyPress}
                            />
                            <FilterTable
                                title="Exclude"
                                pairIndex={index}
                                prefixKey="excludePrefix"
                                suffixKey="excludeSuffix"
                                prefixChips={pair.excludePrefix}
                                suffixChips={pair.excludeSuffix}
                                inputStates={inputStates}
                                onAddChip={onAddChip}
                                onRemoveChip={onRemoveChip}
                                onInputChange={onInputChange}
                                onInputKeyPress={onInputKeyPress}
                            />
                        </div>
                    </div>
                )}

                {pair.addFilter && pair.filterExpanded && errors[`filter_${index}`] && (
                    <ErrorMessage message={errors[`filter_${index}`]} />
                )}
            </div>
        </div>
    );
};

// Internal sub-component: one Include or Exclude table with Prefix + Suffix cells
const FilterTable = ({
    title,
    pairIndex,
    prefixKey,
    suffixKey,
    prefixChips,
    suffixChips,
    inputStates,
    onAddChip,
    onRemoveChip,
    onInputChange,
    onInputKeyPress,
}) => {
    const prefixInputKey = `${pairIndex}-${prefixKey}`;
    const suffixInputKey = `${pairIndex}-${suffixKey}`;

    return (
        <div className="filter-table">
            <h4>{title}</h4>
            <div className="filter-table-content">
                <div className="filter-row">
                    <FilterCell
                        label="Prefix"
                        inputKey={prefixInputKey}
                        chips={prefixChips}
                        inputValue={inputStates[prefixInputKey] || ''}
                        onAdd={() => onAddChip(pairIndex, prefixKey, prefixInputKey)}
                        onRemove={(chipIndex) => onRemoveChip(pairIndex, prefixKey, chipIndex)}
                        onInputChange={(val) => onInputChange(prefixInputKey, val)}
                        onKeyPress={(e) => onInputKeyPress(e, pairIndex, prefixKey, prefixInputKey)}
                    />
                    <div className="filter-divider"></div>
                    <FilterCell
                        label="Suffix"
                        inputKey={suffixInputKey}
                        chips={suffixChips}
                        inputValue={inputStates[suffixInputKey] || ''}
                        onAdd={() => onAddChip(pairIndex, suffixKey, suffixInputKey)}
                        onRemove={(chipIndex) => onRemoveChip(pairIndex, suffixKey, chipIndex)}
                        onInputChange={(val) => onInputChange(suffixInputKey, val)}
                        onKeyPress={(e) => onInputKeyPress(e, pairIndex, suffixKey, suffixInputKey)}
                    />
                </div>
            </div>
        </div>
    );
};

// Internal sub-component: a single Prefix or Suffix cell with input + chips
const FilterCell = ({
    label,
    inputKey,
    chips,
    inputValue,
    onAdd,
    onRemove,
    onInputChange,
    onKeyPress,
}) => (
    <div className="filter-cell">
        <label>{label}</label>
        <div className="filter-input-with-icon">
            <input
                type="text"
                placeholder={`Enter ${label}`}
                value={inputValue}
                onChange={(e) => onInputChange(e.target.value)}
                onKeyDown={onKeyPress}
            />
            <button type="button" className="icon-in-input" onClick={onAdd}>
                <CircleArrowRight style={{ width: '1.042vw', height: '1.042vw' }} color="#2563eb" />
            </button>
        </div>
        <div className="chips-container">
            {chips.map((chip, chipIndex) => (
                <div key={chipIndex} className="chip">
                    <span>{chip}</span>
                    <button type="button" onClick={() => onRemove(chipIndex)}>
                        <X style={{ width: '0.729vw', height: '0.729vw' }} />
                    </button>
                </div>
            ))}
        </div>
    </div>
);

export default SchemaPair;